import { track } from '../features/analytics/analytics';
import { useEffect, useRef, useState } from 'react';
import type { ImageVariant } from '@skytech/shared';
import { variantUrl } from '../lib/format';

/**
 * SRS Appendix B.2 — the single component every product image must go through.
 *
 * Bundles the full §7.6 optimisation matrix: WebP via <picture>, srcset density
 * variants, explicit width/height for zero CLS, lazy vs eager loading, and a
 * skeleton placeholder that occupies the exact final box.
 */
export interface OptimizedImageProps {
  /** Stored `path` prefix from the ProductImage row, e.g. /uploads/products/{id}/ */
  path: string;
  displayOrder: number;
  alt: string;
  /** Which variant drives the intrinsic size. */
  size?: ImageVariant;
  /** Intrinsic dimensions from the ProductImage row — required to prevent CLS. */
  width: number;
  height: number;
  className?: string;
  /** Padding belongs to the image content box, preserving the reserved frame geometry. */
  imageClassName?: string;
  /** Above-the-fold images set this; it flips loading to eager + high priority. */
  priority?: boolean;
  sizes?: string;
  /** Wide single-column mobile cards need a higher-resolution density pair. */
  mobileSize?: 'medium';
  /** Intent-loaded previews are eager without competing with LCP priority. */
  loading?: 'eager' | 'lazy';
  /** Fires only after the browser has decoded the image, including cache hits. */
  onReady?: () => void;
  /**
   * Force a display box ratio (e.g. `"1 / 1"`) instead of the image's intrinsic ratio —
   * used to keep a product gallery visually consistent across mixed source ratios. When
   * set the image is `object-contain` (letterboxed, never cropped) rather than cover.
   */
  aspectRatio?: string;
}

const VARIANT_PX: Record<ImageVariant, number> = { thumbnail: 300, medium: 600, large: 1200 };

function ImageContent({
  path,
  displayOrder,
  alt,
  size = 'medium',
  width,
  height,
  className = '',
  imageClassName = '',
  priority = false,
  sizes,
  aspectRatio,
  loading,
  onReady,
  mobileSize,
}: OptimizedImageProps): JSX.Element {
  const imageRef = useRef<HTMLImageElement>(null);
  const started = useRef(performance.now());
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // Cached and prerendered images may finish before React attaches onLoad.
  useEffect(() => {
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || !onReady) return;
    let cancelled = false;
    const image = imageRef.current;
    if (!image) return;
    const decoded = image.decode ? image.decode() : Promise.resolve();
    void decoded
      .then(() => {
        if (!cancelled) onReady();
      })
      .catch(() => {
        /* Leave primary visible on decode failure. */
      });
    return () => {
      cancelled = true;
    };
  }, [loaded, onReady]);

  const medium = variantUrl(path, displayOrder, 'medium');
  const large = variantUrl(path, displayOrder, 'large');
  const thumb = variantUrl(path, displayOrder, 'thumbnail');

  // 1x/2x density pair per SRS §7.6. Thumbnails step up to medium on retina.
  const srcSet = size === 'thumbnail' ? `${thumb} 1x, ${medium} 2x` : `${medium} 1x, ${large} 2x`;
  const src = size === 'thumbnail' ? thumb : size === 'large' ? large : medium;

  // Scale the declared box to the variant while preserving the true aspect ratio,
  // so the reserved space matches what actually paints.
  const ratio = height / width;
  const boxWidth = VARIANT_PX[size];
  const boxHeight = Math.round(boxWidth * ratio);
  // A forced ratio keeps a gallery visually uniform; it letterboxes (contain) so nothing
  // is cropped. Without it, the intrinsic ratio + cover behaviour is preserved as before.
  const boxAspect = aspectRatio ?? `${width} / ${height}`;
  const objectFit = aspectRatio ? 'object-contain' : 'object-cover';

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center bg-frost text-xs text-navy-700 ${className}`}
        style={{ aspectRatio: boxAspect }}
        role="img"
        aria-label={alt}
      >
        Image unavailable
      </div>
    );
  }

  return (
    <span className={`relative block ${className}`} style={{ aspectRatio: boxAspect }}>
      {!loaded && (
        <span className="absolute inset-0 block rounded-[inherit] bg-frost" aria-hidden />
      )}
      <picture className="absolute inset-0 block">
        {mobileSize && (
          <source
            media="(max-width: 639px)"
            type="image/webp"
            srcSet={`${medium} 1x, ${large} 2x`}
          />
        )}
        <source type="image/webp" srcSet={srcSet} sizes={sizes} />
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          width={boxWidth}
          height={boxHeight}
          loading={priority ? 'eager' : (loading ?? 'lazy')}
          decoding={priority ? 'sync' : 'async'}
          // React 18 does not yet type fetchpriority; the DOM attribute is still emitted.
          {...(priority ? { fetchpriority: 'high' as const } : {})}
          onLoad={() => {
            setLoaded(true);
            if (priority)
              track('product_image_ready', {
                value: Math.round(performance.now() - started.current),
                image_variant: size,
              });
          }}
          onError={() => {
            setFailed(true);
            track('product_image_error', { image_variant: size });
          }}
          className={`relative block h-full w-full ${objectFit} ${imageClassName}`}
        />
      </picture>
    </span>
  );
}

/** Reset load/error state whenever the actual asset identity changes (§7.6). */
export function OptimizedImage(props: OptimizedImageProps): JSX.Element {
  return (
    <ImageContent
      key={`${props.path}:${props.displayOrder}:${props.size ?? 'medium'}`}
      {...props}
    />
  );
}
